import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../game/config';
import { deckService } from '../services/DeckService';
import { runState } from '../services/RunState';
import { profileStore } from '../services/ProfileStore';
import { gameBridge } from '../services/GameBridge';
import { WORLD_TILES, MONSTERS, randomElement, getCharacterOption } from '../config/tileIndex';
import { addHalo, getPaletteForSubject, pickFloorFrame, pickWallFrame, shouldPlaceDecal, pickDecalFrame } from '../services/TilePalette';
import type { FlashCard } from '../types/deck';

interface JudgmentSceneData {
  cardId: string;
  subject?: string;
  card?: FlashCard;
  playerAnswer?: string;
  correctAnswer?: string;
}

const SCALE = 3;
const TILE_SIZE = 16;
const SCALED_TILE = TILE_SIZE * SCALE;

/**
 * Judgment Scene - Shows remediation after wrong answer
 * Features a courtroom setting with judge character
 * UI is rendered by React (JudgmentUI.tsx), this scene handles:
 * - Scene transitions
 * - Audio playback
 * - Courtroom background rendering
 */
export class JudgmentScene extends Phaser.Scene {
  private cardId!: string;
  private subject!: string;
  private card!: FlashCard;
  private playerAnswer!: string;
  private correctAnswer!: string;
  private palette = getPaletteForSubject(''); // set on init
  private rematchSubscription?: () => void;
  private continueSubscription?: () => void;
  private returnSubscription?: () => void;

  // Visual elements
  private judge!: Phaser.GameObjects.Sprite;
  private player!: Phaser.GameObjects.Sprite;
  private gavel!: Phaser.GameObjects.Sprite;

  constructor() {
    super({ key: 'JudgmentScene' });
  }

  init(data: JudgmentSceneData): void {
    this.palette = getPaletteForSubject(data.subject || '');
    this.cardId = data.cardId;
    this.subject = data.subject || '';
    this.playerAnswer = data.playerAnswer || '';
    this.correctAnswer = data.correctAnswer || '';

    // Use passed card or fetch it
    if (data.card) {
      this.card = data.card;
    }
  }

  create(): void {
    // Get card if not passed
    if (!this.card) {
      const card = deckService.getCardById(this.cardId);
      if (!card) {
        console.error(`Card not found: ${this.cardId}`);
        this.returnToHub();
        return;
      }
      this.card = card;
    }

    // Play judgment sound if enabled
    if (profileStore.isAudioEnabled() && this.sound.get('judgment')) {
      this.sound.play('judgment', { volume: 0.5 });
    }

    // Render courtroom background
    this.createCourtroom();

    // Create judge character
    this.createJudge();

    // Create player character (defendant)
    this.createPlayer();

    // Add atmospheric effects
    this.addAtmosphere();

    // Emit scene change event
    gameBridge.emit('scene-changed', { scene: 'judgment' });

    // Emit judgment started event with all data React needs
    gameBridge.emit('judgment-started', {
      card: this.card,
      wasCorrect: false,
      playerAnswer: this.playerAnswer,
      correctAnswer: this.correctAnswer,
      subject: this.subject,
    });

    // Subscribe to React events
    this.setupEventListeners();
  }

  /**
   * Create courtroom background with marble floors and formal setting
   */
  private createCourtroom(): void {
    // Calculate courtroom dimensions
    const courtroomWidth = 13;
    const courtroomHeight = 9;
    const offsetX = (GAME_WIDTH - courtroomWidth * SCALED_TILE) / 2;
    const offsetY = (GAME_HEIGHT - courtroomHeight * SCALED_TILE) / 2 - 20;

    // Draw floor tiles
    for (let y = 1; y < courtroomHeight - 1; y++) {
      for (let x = 1; x < courtroomWidth - 1; x++) {
        const isCenterAisle = x >= 5 && x <= 7;
        const baseTile = pickFloorFrame(x, y, courtroomWidth, courtroomHeight, this.palette);
        const floorTile = isCenterAisle
          ? randomElement([WORLD_TILES.FLOOR_CARPET_A, WORLD_TILES.FLOOR_CARPET_C])
          : baseTile;

        const sprite = this.add.sprite(
          offsetX + x * SCALED_TILE + SCALED_TILE / 2,
          offsetY + y * SCALED_TILE + SCALED_TILE / 2,
          'world',
          floorTile
        );
        sprite.setScale(SCALE);

        if (!isCenterAisle && shouldPlaceDecal(this.palette)) {
          const decal = this.add.sprite(
            sprite.x,
            sprite.y,
            'world',
            pickDecalFrame(this.palette)
          );
          decal.setScale(SCALE);
          decal.setDepth(1);
          decal.setAlpha(0.6);
        }
      }
    }

    // Draw walls - stone for formal setting
    const wallTilesH = [WORLD_TILES.WALL_STONE_H_A, WORLD_TILES.WALL_STONE_H_B, WORLD_TILES.WALL_STONE_H_E];
    const wallTilesV = [WORLD_TILES.WALL_STONE_V_A, WORLD_TILES.WALL_STONE_V_B, WORLD_TILES.WALL_STONE_V_E];

    // Top and bottom walls
    for (let x = 0; x < courtroomWidth; x++) {
      const topWall = this.add.sprite(
        offsetX + x * SCALED_TILE + SCALED_TILE / 2,
        offsetY + SCALED_TILE / 2,
        'world',
        pickWallFrame(true, this.palette) || randomElement(wallTilesH)
      );
      topWall.setScale(SCALE);

      const bottomWall = this.add.sprite(
        offsetX + x * SCALED_TILE + SCALED_TILE / 2,
        offsetY + (courtroomHeight - 1) * SCALED_TILE + SCALED_TILE / 2,
        'world',
        pickWallFrame(true, this.palette) || randomElement(wallTilesH)
      );
      bottomWall.setScale(SCALE);
    }

    // Left and right walls
    for (let y = 1; y < courtroomHeight - 1; y++) {
      const leftWall = this.add.sprite(
        offsetX + SCALED_TILE / 2,
        offsetY + y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        pickWallFrame(false, this.palette) || randomElement(wallTilesV)
      );
      leftWall.setScale(SCALE);

      const rightWall = this.add.sprite(
        offsetX + (courtroomWidth - 1) * SCALED_TILE + SCALED_TILE / 2,
        offsetY + y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        pickWallFrame(false, this.palette) || randomElement(wallTilesV)
      );
      rightWall.setScale(SCALE);
    }

    if (this.palette.ambientColor) {
      const overlay = this.add.rectangle(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        GAME_WIDTH,
        GAME_HEIGHT,
        this.palette.ambientColor,
        0.1
      );
      overlay.setDepth(1).setScrollFactor(0);
    }

    // Add judge's bench (using table and bookshelves)
    const benchY = 2;
    const benchCenterX = Math.floor(courtroomWidth / 2);

    // Central table for judge
    const benchTable = this.add.sprite(
      offsetX + benchCenterX * SCALED_TILE + SCALED_TILE / 2,
      offsetY + benchY * SCALED_TILE + SCALED_TILE / 2,
      'world',
      WORLD_TILES.TABLE
    );
    benchTable.setScale(SCALE);

    // Bookshelves on either side
    const leftBookshelf = this.add.sprite(
      offsetX + (benchCenterX - 2) * SCALED_TILE + SCALED_TILE / 2,
      offsetY + benchY * SCALED_TILE + SCALED_TILE / 2,
      'world',
      WORLD_TILES.BOOKSHELF_A
    );
    leftBookshelf.setScale(SCALE);

    const rightBookshelf = this.add.sprite(
      offsetX + (benchCenterX + 2) * SCALED_TILE + SCALED_TILE / 2,
      offsetY + benchY * SCALED_TILE + SCALED_TILE / 2,
      'world',
      WORLD_TILES.BOOKSHELF_B
    );
    rightBookshelf.setScale(SCALE);

    // Add candles for lighting
    const candlePositions = [
      { x: 2, y: 2 },
      { x: courtroomWidth - 3, y: 2 },
      { x: 2, y: courtroomHeight - 3 },
      { x: courtroomWidth - 3, y: courtroomHeight - 3 },
    ];

    for (const pos of candlePositions) {
      const candle = this.add.sprite(
        offsetX + pos.x * SCALED_TILE + SCALED_TILE / 2,
        offsetY + pos.y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        WORLD_TILES.CANDLES_A
      );
      candle.setScale(SCALE);
      candle.play('candle-flicker');
      // Add warm halo light
      addHalo(this, candle.x, candle.y, 0xffcc66, SCALED_TILE * 0.8, 0.18);
    }

    // Add throne for judge
    const throne = this.add.sprite(
      offsetX + benchCenterX * SCALED_TILE + SCALED_TILE / 2,
      offsetY + 1 * SCALED_TILE + SCALED_TILE / 2,
      'world',
      WORLD_TILES.THRONE
    );
    throne.setScale(SCALE);

    // Add banners
    const leftBanner = this.add.sprite(
      offsetX + 3 * SCALED_TILE + SCALED_TILE / 2,
      offsetY + 1 * SCALED_TILE + SCALED_TILE / 2,
      'world',
      WORLD_TILES.BANNER_A
    );
    leftBanner.setScale(SCALE);

    const rightBanner = this.add.sprite(
      offsetX + (courtroomWidth - 4) * SCALED_TILE + SCALED_TILE / 2,
      offsetY + 1 * SCALED_TILE + SCALED_TILE / 2,
      'world',
      WORLD_TILES.BANNER_B
    );
    rightBanner.setScale(SCALE);

    // Add somber red tint
    const overlay = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x880000,
      0.15
    );
    overlay.setDepth(0);
  }

  /**
   * Create judge character
   */
  private createJudge(): void {
    // Position judge behind bench
    this.judge = this.add.sprite(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 3 - 20,
      'monsters',
      MONSTERS.WIZARD // Wise-looking character as judge
    );
    this.judge.setScale(SCALE * 1.8);
    this.judge.setDepth(10);

    // Add authoritative golden halo behind judge
    addHalo(this, GAME_WIDTH / 2, GAME_HEIGHT / 3 - 20, 0xffdd88, SCALED_TILE * 1.4, 0.22);

    // Add subtle floating animation
    this.tweens.add({
      targets: this.judge,
      y: this.judge.y - 4,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Entrance animation
    this.judge.setAlpha(0);
    this.tweens.add({
      targets: this.judge,
      alpha: 1,
      duration: 500,
      ease: 'Power2',
    });

    // Add gavel near judge (using a prop)
    this.gavel = this.add.sprite(
      this.judge.x + 60,
      this.judge.y + 20,
      'items',
      12 // Hammer item
    );
    this.gavel.setScale(SCALE);
    this.gavel.setDepth(11);

    // Gavel slam animation
    this.time.delayedCall(800, () => {
      this.gavelSlam();
    });
  }

  /**
   * Animate gavel slam
   */
  private gavelSlam(): void {
    this.tweens.add({
      targets: this.gavel,
      angle: -30,
      duration: 100,
      yoyo: true,
      onComplete: () => {
        // Screen shake on slam
        this.cameras.main.shake(100, 0.005);
      },
    });

    // Repeat slam after delay
    this.time.delayedCall(3000, () => {
      if (this.gavel && this.gavel.active) {
        this.gavelSlam();
      }
    });
  }

  /**
   * Create player character (defendant)
   */
  private createPlayer(): void {
    const charOption = getCharacterOption(profileStore.getSelectedCharacter());
    const isHighResAtlas = charOption.texture.startsWith('k1_kim');

    if (isHighResAtlas) {
      // Kim uses high-res atlas (192x272 frames)
      const firstFrame = `${charOption.texture}_0001`;
      this.player = this.add.sprite(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 140,
        charOption.texture,
        firstFrame
      );
      // Scale appropriately for judgment display
      this.player.setScale(0.5);
    } else {
      this.player = this.add.sprite(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 140,
        charOption.texture || 'monsters',
        charOption.frame
      );
      this.player.setScale(SCALE * 1.5);
    }
    this.player.setDepth(10);

    // Try to play hurt/defeat animation for judgment scene
    const hurtAnim = `${charOption.id}-hurt`;
    if (this.anims.exists(hurtAnim)) {
      this.player.play(hurtAnim);
    }

    // Sad/guilty pose - head slightly down
    this.player.setAngle(-5);

    // Slight swaying (nervous)
    this.tweens.add({
      targets: this.player,
      angle: 5,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Entrance animation
    this.player.setAlpha(0);
    this.player.y = GAME_HEIGHT;
    this.tweens.add({
      targets: this.player,
      y: GAME_HEIGHT - 140,
      alpha: 1,
      duration: 500,
      ease: 'Power2',
    });
  }

  /**
   * Add atmospheric effects
   */
  private addAtmosphere(): void {
    // Dim lighting effect at edges
    const vignette = this.add.graphics();
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.5, 0.5, 0, 0);
    vignette.fillRect(0, 0, GAME_WIDTH, 50);
    vignette.fillRect(0, GAME_HEIGHT - 50, GAME_WIDTH, 50);

    // Floating dust motes
    for (let i = 0; i < 3; i++) {
      const dust = this.add.circle(
        Phaser.Math.Between(100, GAME_WIDTH - 100),
        Phaser.Math.Between(100, GAME_HEIGHT - 100),
        2,
        0xffffcc,
        0.2
      );

      this.tweens.add({
        targets: dust,
        y: dust.y - 30,
        alpha: 0,
        duration: Phaser.Math.Between(3000, 5000),
        repeat: -1,
        delay: Phaser.Math.Between(0, 2000),
        onRepeat: () => {
          dust.x = Phaser.Math.Between(100, GAME_WIDTH - 100);
          dust.y = Phaser.Math.Between(150, GAME_HEIGHT - 100);
          dust.alpha = 0.2;
        },
      });
    }
  }

  private setupEventListeners(): void {
    // Listen for rematch request from React UI
    this.rematchSubscription = gameBridge.subscribe('rematch-requested', () => {
      this.startRematch();
    });

    // Listen for continue request
    this.continueSubscription = gameBridge.subscribe('continue-run', () => {
      this.continueRun();
    });

    // Listen for return to hub request
    this.returnSubscription = gameBridge.subscribe('return-to-hub', () => {
      this.returnToHub();
    });
  }

  private startRematch(): void {
    // Exit animation
    this.tweens.add({
      targets: [this.player, this.judge, this.gavel],
      alpha: 0,
      duration: 300,
      onComplete: () => {
        this.cleanup();
        this.scene.start('EncounterScene', {
          cardId: this.cardId,
          subject: this.subject,
          isRematch: true,
        });
      },
    });
  }

  private continueRun(): void {
    const nextCard = runState.getNextCard();

    if (nextCard) {
      this.cleanup();
      this.scene.start('EncounterScene', {
        cardId: nextCard.id,
        subject: this.subject,
        isRematch: false,
      });
    } else {
      this.returnToHub();
    }
  }

  private returnToHub(): void {
    // Exit animation
    this.tweens.add({
      targets: [this.player],
      y: GAME_HEIGHT + 100,
      alpha: 0,
      duration: 400,
      onComplete: () => {
        this.cleanup();
        runState.endRun();
        gameBridge.emit('scene-changed', { scene: 'hub' });
        this.scene.start('WorldScene');
      },
    });
  }

  private cleanup(): void {
    if (this.rematchSubscription) {
      this.rematchSubscription();
      this.rematchSubscription = undefined;
    }
    if (this.continueSubscription) {
      this.continueSubscription();
      this.continueSubscription = undefined;
    }
    if (this.returnSubscription) {
      this.returnSubscription();
      this.returnSubscription = undefined;
    }
  }

  shutdown(): void {
    this.cleanup();
  }
}
